"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useI18n } from "./I18nProvider";

export function MarkdownCodeBlock({ value, language }: { value: string; language?: string }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const busy = useRef(false);

  useEffect(() => {
    if (status === "idle") return;
    const timer = window.setTimeout(() => setStatus("idle"), 2500);
    return () => window.clearTimeout(timer);
  }, [status]);

  async function copy() {
    if (busy.current) return;
    busy.current = true;
    setStatus("idle");
    const copied = await copyTextToClipboard(value);
    setStatus(copied ? "copied" : "failed");
    busy.current = false;
  }

  return (
    <div className="markdown-code-container">
      <div className="markdown-code-toolbar">
        <span className="markdown-code-language">{language}</span>
        <button type="button" className="markdown-code-copy" onClick={copy}>
          {status === "copied" ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          <span aria-live="polite">{t(status === "copied" ? "invite.copied" : status === "failed" ? "share.copyFailed" : "invite.copy")}</span>
        </button>
      </div>
      <pre className="markdown-code-block" data-language={language} tabIndex={0}>
        <code data-language={language}>{value}</code>
      </pre>
    </div>
  );
}
